import { createRoot } from "react-dom/client";
import { captureException } from "@sentry/react";
import "./index.css";
import { initSentry } from "./config/sentry";
import { redirectLoopbackToPreferredHost } from "./lib/local-hostname";

const ROOT_ELEMENT_ID = "root";

async function bootstrap() {
  // Initialize Sentry before loading the router so route tracing can wrap it correctly.
  initSentry();

  const { default: App } = await import("./App.tsx");
  const container = document.getElementById(ROOT_ELEMENT_ID);
  if (!container) {
    throw new Error("Root element not found");
  }

  const root = createRoot(container);
  root.render(<App />);
}

function renderBootstrapFailure(error: unknown) {
  console.error("Failed to bootstrap application", error);
  try {
    captureException(error);
  } catch {
    // Sentry may be unavailable if initialization did not complete.
  }
  const container = document.getElementById(ROOT_ELEMENT_ID);
  if (!container) return;
  container.textContent = "";
  const banner = document.createElement("div");
  banner.setAttribute("role", "alert");
  banner.style.boxSizing = "border-box";
  banner.style.padding = "2rem";
  banner.style.maxWidth = "32rem";
  banner.style.margin = "2rem auto";
  banner.style.fontFamily = "system-ui, sans-serif";
  banner.style.lineHeight = "1.5";
  banner.textContent =
    "The application failed to load. Try a hard refresh (reload bypassing cache: Ctrl+Shift+R or Cmd+Shift+R). If the problem continues, contact support.";
  container.appendChild(banner);
  if (import.meta.env.DEV) {
    const detail =
      error instanceof Error
        ? `${error.name}: ${error.message}`
        : typeof error === "string"
          ? error
          : "Unknown bootstrap error";
    const detailEl = document.createElement("pre");
    detailEl.style.marginTop = "1rem";
    detailEl.style.whiteSpace = "pre-wrap";
    detailEl.style.fontSize = "0.85rem";
    detailEl.style.color = "#b91c1c";
    detailEl.textContent = detail;
    container.appendChild(detailEl);
  }
}

async function startApp() {
  // GoTrue magic links (impersonation) may land on 127.0.0.1 while the Hub uses localhost.
  if (redirectLoopbackToPreferredHost()) {
    return;
  }
  await bootstrap();
}

void startApp().catch(renderBootstrapFailure);
